import { watch } from 'vue'
import { safeRedirect } from '@/utils/safeRedirect'
import { useFeatureStore } from '@/stores/feature'
import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useI18n } from '@/composables/useI18n'

// Code splitting: only the landing page ships in the entry chunk — every other
// route (and the entire admin suite) is split into its own lazy chunk so
// storefront visitors never download the admin panel bundle.
import LandingPage from '@/pages/LandingPage.vue'
import { adminPanelUrl } from '@/utils/siteHosts'
import { onPageMetaChange, pageMetaFor } from './pageMeta'

const LiveAuctionsPage = () => import('@/pages/LiveAuctionsPage.vue')
const AuctionDetailPage = () => import('@/pages/AuctionDetailPage.vue')
const CategoriesPage = () => import('@/pages/CategoriesPage.vue')
const SellPage = () => import('@/pages/SellPage.vue')
const DashboardPage = () => import('@/pages/DashboardPage.vue')
const LoginPage = () => import('@/pages/LoginPage.vue')
const RegisterPage = () => import('@/pages/RegisterPage.vue')
const ForgotPasswordPage = () => import('@/pages/ForgotPasswordPage.vue')
const ResetPasswordPage = () => import('@/pages/ResetPasswordPage.vue')
const VerifyEmailPage = () => import('@/pages/VerifyEmailPage.vue')
const HowItWorksPage = () => import('@/pages/HowItWorksPage.vue')
const AboutPage = () => import('@/pages/AboutPage.vue')
const ContactPage = () => import('@/pages/ContactPage.vue')
const PrivacyPage = () => import('@/pages/PrivacyPage.vue')
const TermsPage = () => import('@/pages/TermsPage.vue')
const NotFoundPage = () => import('@/pages/NotFoundPage.vue')
const SellerPage = () => import('@/pages/SellerPage.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'LandingPage',
    component: LandingPage,
    meta: { titleKey: 'pageTitle.home', requiresAuth: false }
  },
  {
    path: '/auctions',
    name: 'LiveAuctions',
    component: LiveAuctionsPage,
    alias: ['/canli-acik-artirmalar', '/canlı-açık-artırmalar', '/аукциондор', '/аукционы'],
    meta: { titleKey: 'nav.auctions', requiresAuth: false }
  },
  {
    path: '/auctions/:id',
    name: 'AuctionDetail',
    component: AuctionDetailPage,
    alias: ['/ilan-detail/:id', '/ilan/:id', '/auction/:id'],
    props: true,
    meta: { titleKey: 'pageTitle.auctionDetail', requiresAuth: false }
  },
  {
    path: '/sellers/:id',
    name: 'SellerProfile',
    component: SellerPage,
    // Indexed: sellers are named publicly only as "Hakan A." (server shortName).
    meta: { titleKey: 'sellerPage.title', requiresAuth: false }
  },
  {
    path: '/categories',
    name: 'Categories',
    component: CategoriesPage,
    alias: ['/kategoriler', '/категориялар', '/категории'],
    meta: { titleKey: 'categoriesPage.title', requiresAuth: false }
  },
  {
    path: '/sell',
    name: 'Sell',
    component: SellPage,
    alias: ['/sat', '/ilan-ver', '/сатуу', '/продать'],
    meta: { titleKey: 'sell.title', requiresAuth: false }
  },
  {
    path: '/dashboard/:tab?',
    name: 'Dashboard',
    component: DashboardPage,
    alias: ['/dashboard', '/profil', '/profile', '/hesabim'],
    props: true,
    meta: { titleKey: 'dashboard.title', requiresAuth: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    alias: ['/giris', '/auth/login'],
    meta: { titleKey: 'nav.login', requiresAuth: false }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    alias: ['/kayit', '/kaydol', '/auth/register'],
    meta: { titleKey: 'nav.register', requiresAuth: false }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPasswordPage,
    alias: ['/sirsozdu-unuttum', '/sifremi-unuttum'],
    meta: { titleKey: 'auth.forgotPasswordTitle', requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPasswordPage,
    meta: { titleKey: 'auth.resetPasswordTitle', requiresAuth: false }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: VerifyEmailPage,
    meta: { titleKey: 'auth.verifyEmailTitle', requiresAuth: false }
  },
  {
    path: '/how-it-works',
    name: 'HowItWorks',
    component: HowItWorksPage,
    alias: ['/nasil-calisir', '/nasıl-çalışır'],
    meta: { titleKey: 'hiw.title', requiresAuth: false }
  },
  {
    path: '/about',
    name: 'About',
    component: AboutPage,
    alias: ['/hakkimizda', '/hakkımızda'],
    meta: { titleKey: 'aboutPage.title', requiresAuth: false }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: ContactPage,
    alias: ['/iletisim', '/iletişim'],
    meta: { titleKey: 'contactPage.title', requiresAuth: false }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: PrivacyPage,
    alias: ['/gizlilik', '/gizlilik-politikasi'],
    meta: { titleKey: 'privacyPage.title', requiresAuth: false }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: TermsPage,
    alias: ['/kullanim-kosullari', '/terms-of-service'],
    meta: { titleKey: 'termsPage.title', requiresAuth: false }
  },
  // Standalone Admin Panel Redirect
  {
    path: '/admin/:pathMatch(.*)*',
    redirect: () => {
      if (typeof window !== 'undefined') {
        window.location.href = adminPanelUrl()
      }
      return '/dashboard'
    }
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundPage,
    meta: { titleKey: 'notFound.title', requiresAuth: false }
  }
]

const router = createRouter({
  history: createWebHistory((import.meta as any).env?.BASE_URL || '/'),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    // A new page starts at the top instantly (it is entering anyway); only
    // same-page changes such as filters glide back up.
    if (to.path !== from.path) return { top: 0 }
    return { top: 0, behavior: 'smooth' }
  }
})

// Route protection
router.beforeEach((to, _from, next) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null
  const userJson = typeof window !== 'undefined' ? localStorage.getItem('user') : null
  let user: any = null
  if (userJson) {
    try { user = JSON.parse(userJson) } catch {}
  }

  // General auth route check
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!token || !user) {
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }
  }

  // A signed-in user has nothing to do on the login/register forms; send them
  // on to where they were headed (or the dashboard).
  if (token && user && (to.name === 'Login' || to.name === 'Register')) {
    return next(safeRedirect(to.query.redirect))
  }

  next()
})

const { t, locale } = useI18n()

// Admin Settings > Platform holds the home page title and meta description
// once per language. An empty entry means "use the storefront's own
// translation". A plain string is what an older server sends; its language is
// unknown, so it is ignored rather than guessed at.
function siteText(value: unknown, lang: string): string {
  if (!value || typeof value !== 'object') return ''
  const text = (value as Record<string, unknown>)[lang]
  return typeof text === 'string' ? text.trim() : ''
}

function applyDocumentTitle() {
  const current = router.currentRoute.value
  const meta = current.meta
  let site: { name?: string; title?: unknown; description?: unknown } | undefined
  try { site = useFeatureStore().config.site } catch { site = undefined }
  const brand = site?.name?.trim() || 'iTorgo'
  const isHome = current.path === '/'
  const homeTitle = siteText(site?.title, locale.value)
  if (isHome && homeTitle) {
    document.title = homeTitle
  } else if (isHome) {
    document.title = `${brand} — ${t('pageTitle.home')}`
  } else if (typeof meta?.titleKey === 'string') {
    document.title = `${brand} — ${t(meta.titleKey)}`
  } else if (typeof meta?.title === 'string') {
    document.title = `${brand} — ${meta.title}`
  } else {
    document.title = `${brand} — ${t('pageTitle.home')}`
  }
  // A lot or a seller page that has loaded names itself (router/pageMeta.ts).
  const own = pageMetaFor(current.path)
  if (own) document.title = own.title()
  // Site description everywhere; a page with its own (a lot) replaces it
  // after its data loads.
  const description = siteText(site?.description, locale.value) || t('pageMeta.homeDescription')
  document.querySelector('meta[name="description"]')?.setAttribute('content', own?.description?.() || description)

  // Dynamic Robots Meta (Noindex for admin & dashboard, index for public)
  let robotsMeta = document.querySelector('meta[name="robots"]')
  if (!robotsMeta) {
    robotsMeta = document.createElement('meta')
    robotsMeta.setAttribute('name', 'robots')
    document.head.appendChild(robotsMeta)
  }

  const isPrivate = current.path.startsWith('/admin') || current.path.startsWith('/dashboard')
  if (isPrivate) {
    robotsMeta.setAttribute('content', 'noindex, nofollow')
  } else if (typeof meta?.robots === 'string') {
    robotsMeta.setAttribute('content', meta.robots)
  } else {
    robotsMeta.setAttribute('content', 'index, follow, max-image-preview:large')
  }

  // Dynamic Canonical Link
  let canonicalLink = document.querySelector('link[rel="canonical"]')
  if (!canonicalLink) {
    canonicalLink = document.createElement('link')
    canonicalLink.setAttribute('rel', 'canonical')
    document.head.appendChild(canonicalLink)
  }
  // One address for search engines (itorgo.kg, also for the itorgo.com
  // mirror). Only the category query makes a different page; sort, filters
  // and paging are the same listing.
  const category = typeof current.query.category === 'string' ? current.query.category : ''
  const canonicalPath = category && current.path === '/auctions'
    ? `${current.path}?category=${encodeURIComponent(category)}`
    : current.path
  canonicalLink.setAttribute('href', `https://itorgo.kg${canonicalPath}`)
}

let siteWatchArmed = false
router.afterEach(() => {
  applyDocumentTitle()
  // Public config usually lands after the first navigation; re-apply once it does.
  if (!siteWatchArmed) {
    siteWatchArmed = true
    try { watch(() => useFeatureStore().config.site, applyDocumentTitle, { deep: true }) } catch { /* pinia not ready */ }
  }
})

// Route meta only re-evaluates on navigation; switching the language while
// staying on the same page needs its own trigger so the tab title doesn't
// stay stale until the next navigation.
watch(locale, applyDocumentTitle)
onPageMetaChange(applyDocumentTitle)

export { router }